import { usePageTitle } from '../hooks/usePageTitle';
import React, { useEffect, useState } from 'react';
import api, { BASE_URL, fotoUrl } from '../api';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../AuthContext';

const Ogrenciler: React.FC = () => {
  usePageTitle('Öğrenciler');
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { kullanici } = useAuth();
  const superAdmin = kullanici?.rol === 'super_admin';
  const card        = isDark ? '#1a1a1a' : 'white';
  const cardAlt     = isDark ? '#1e1e1e' : '#f8f8f8';
  const cardHover   = isDark ? '#252525' : '#fafafa';
  const border      = isDark ? '#2a2a2a' : '#eee';
  const borderSub   = isDark ? '#202020' : '#f0f0f0';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec     = isDark ? '#999'    : '#888';
  const textMuted   = isDark ? '#666'    : '#aaa';
  const [ogrenciler, setOgrenciler] = useState<any[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [arama, setArama] = useState('');
  const [filtre, setFiltre] = useState<'aktif' | 'pasif' | 'tum' | 'hak_var'>('aktif');
  const [sayilar, setSayilar] = useState<{ aktif: number; pasif: number; toplam: number; hak_var: number } | null>(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ ad: '', soyad: '', email: '', tel: '', seviye: 'Başlangıç', cinsiyet: '', dogum_tarihi: '' });
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [hata, setHata] = useState('');
  const [mevcutOgrenciId, setMevcutOgrenciId] = useState('');  // "zaten bu şubede" hatasında profile kısayol
  const [siralama, setSiralama] = useState<'ad' | 'son_yoklama_yeni' | 'son_yoklama_eski'>('ad');
  // Süper admin: şube filtresi + öğrenciyi şubeye taşıma / ek şube ekleme
  const [subeler, setSubeler] = useState<{ id: string; ad: string }[]>([]);
  const [subeFiltre, setSubeFiltre] = useState('');
  const [subeModal, setSubeModal] = useState<any | null>(null);
  const [yeniAnaSube, setYeniAnaSube] = useState('');
  const [yeniEkSube, setYeniEkSube] = useState('');
  const [subeIslem, setSubeIslem] = useState(false);

  const yukle = () => {
    setYukleniyor(true);
    const q = new URLSearchParams();
    if (filtre === 'tum') q.set('tum', 'true');
    if (filtre === 'pasif') q.set('pasif', 'true');
    if (subeFiltre) q.set('sube_id', subeFiltre);
    const params = q.toString() ? '?' + q.toString() : '';
    api.get('/api/ogrenciler' + params)  // hak_var için aktif liste çekilir, client-side filtre
      .then(res => setOgrenciler(res.data))
      .catch(err => console.error(err))
      .finally(() => setYukleniyor(false));
  };

  useEffect(() => { yukle(); }, [filtre, subeFiltre]);

  const sayilariYukle = () => {
    api.get('/api/ogrenciler/sayilar' + (subeFiltre ? `?sube_id=${subeFiltre}` : ''))
      .then(res => setSayilar(res.data))
      .catch(() => {});
  };
  useEffect(() => { sayilariYukle(); }, [subeFiltre]);

  useEffect(() => {
    if (!superAdmin) return;
    api.get('/api/subeler/public').then(r => setSubeler(r.data)).catch(() => {});
  }, [superAdmin]);

  const subeModalAc = (o: any) => {
    setSubeModal(o); setYeniAnaSube(o.sube_id || ''); setYeniEkSube('');
  };

  // Şube işlemlerinden sonra hem listeyi hem açık pencereyi tazele
  const subeIslemiYap = async (istek: () => Promise<any>, basariMesaji: string) => {
    if (!subeModal) return;
    setSubeIslem(true);
    try {
      await istek();
      const r = await api.get(`/api/ogrenciler?tum=true&arama=${encodeURIComponent(subeModal.email || subeModal.ad)}`, { timeout: 15000 });
      const guncel = r.data.find((x: any) => x.id === subeModal.id);
      if (guncel) { setSubeModal(guncel); setYeniAnaSube(guncel.sube_id || ''); }
      setYeniEkSube('');
      yukle(); sayilariYukle();
      alert(basariMesaji);
    } catch (err: any) {
      alert(err.response?.data?.hata || (err.code === 'ECONNABORTED' ? 'Sunucu yanıt vermedi, tekrar deneyin' : 'İşlem başarısız'));
    } finally { setSubeIslem(false); }
  };

  const filtrelenmis = ogrenciler
    .filter(o => {
      if (filtre === 'hak_var' && !o.dersler?.some((d: any) => d.kalan_hak > 0)) return false;
      return `${o.ad} ${o.soyad} ${o.email} ${o.tel || ''}`.toLowerCase().includes(arama.toLowerCase());
    })
    .sort((a, b) => {
      if (siralama === 'ad') return `${a.ad} ${a.soyad}`.localeCompare(`${b.ad} ${b.soyad}`, 'tr');
      const ta = a.son_yoklama_tarihi ? new Date(a.son_yoklama_tarihi).getTime() : 0;
      const tb = b.son_yoklama_tarihi ? new Date(b.son_yoklama_tarihi).getTime() : 0;
      return siralama === 'son_yoklama_yeni' ? tb - ta : ta - tb;
    });

  const kaydet = async () => {
    if (!form.ad || !form.soyad || !form.email) { setHata('Ad, soyad ve email zorunlu'); return; }
    setKaydediliyor(true); setHata(''); setMevcutOgrenciId('');
    try {
      if (superAdmin && !subeFiltre) { setHata('Öğrenci eklemek için önce yukarıdan bir şube seçin'); return; }
      const r = await api.post('/api/ogrenciler', superAdmin ? { ...form, sube_id: subeFiltre } : form, { timeout: 15000 });
      setModal(false);
      setForm({ ad: '', soyad: '', email: '', tel: '', seviye: 'Başlangıç', cinsiyet: '', dogum_tarihi: '' });
      yukle(); sayilariYukle();
      // Email başka şubede/uygulamada kayıtlıysa yeni hesap açılmaz, mevcut hesap şubeye bağlanır
      if (r.data?.mesaj) alert(r.data.mesaj);
    } catch (err: any) {
      setHata(err.response?.data?.hata || (err.code === 'ECONNABORTED' ? 'Sunucu yanıt vermedi, tekrar deneyin' : 'Hata oluştu'));
      setMevcutOgrenciId(err.response?.data?.ogrenci_id || '');
    } finally {
      setKaydediliyor(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Öğrenciler</h1>
        <button onClick={() => setModal(true)} style={{ background: '#534AB7', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
          + Öğrenci ekle
        </button>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {([
          { key: 'aktif',   label: 'Aktif Öğrenciler',   sayi: sayilar?.aktif },
          { key: 'hak_var', label: 'Ders Hakkı Var',      sayi: sayilar?.hak_var },
          { key: 'pasif',   label: 'Pasif Öğrenciler',    sayi: sayilar?.pasif },
          { key: 'tum',     label: 'Tüm Öğrenciler',      sayi: sayilar?.toplam },
        ] as const).map(({ key, label, sayi }) => (
          <button key={key} onClick={() => setFiltre(key)}
            style={{ padding: '6px 14px', borderRadius: 20, border: '1px solid', fontSize: 12, cursor: 'pointer',
              background: filtre === key ? '#534AB7' : card,
              color: filtre === key ? 'white' : textSec,
              borderColor: filtre === key ? '#534AB7' : '#ddd' }}>
            {label}{sayi != null ? ` (${sayi})` : ''}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <input
          value={arama}
          onChange={e => setArama(e.target.value)}
          placeholder="İsim veya email ara..."
          style={{ flex: 1, padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }}
        />
        {superAdmin && (
          <select value={subeFiltre} onChange={e => setSubeFiltre(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, background: card, color: textPrimary }}>
            <option value="">Tüm şubeler</option>
            {subeler.map(s => <option key={s.id} value={s.id}>{s.ad}</option>)}
          </select>
        )}
      </div>

      <div style={{ background: card, borderRadius: 10, border: `1px solid ${border}`, overflow: 'hidden' }}>
        {!yukleniyor && filtrelenmis.length > 0 && (
          <div style={{ padding: '10px 16px', borderBottom: `1px solid ${borderSub}`, fontSize: 12, color: textSec }}>
            <span style={{ fontWeight: 600, color: '#534AB7' }}>{filtrelenmis.length}</span> öğrenci
          </div>
        )}
        {yukleniyor ? (
          <div style={{ padding: 24, textAlign: 'center', color: textSec, fontSize: 13 }}>Yükleniyor...</div>
        ) : filtrelenmis.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: textSec, fontSize: 13 }}>
            {ogrenciler.length === 0 ? 'Henüz öğrenci yok.' : 'Sonuç bulunamadı.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${border}` }}>
                <th style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, color: textMuted, fontWeight: 500, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => setSiralama('ad')}>
                  Öğrenci {siralama === 'ad' ? '↑' : ''}
                </th>
                {superAdmin && <th style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, color: textMuted, fontWeight: 500 }}>Şube</th>}
                <th style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, color: textMuted, fontWeight: 500 }}>Seviye</th>
                <th style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, color: textMuted, fontWeight: 500 }}>Dersler</th>
                <th style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, color: textMuted, fontWeight: 500, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                  onClick={() => setSiralama(s => s === 'son_yoklama_yeni' ? 'son_yoklama_eski' : 'son_yoklama_yeni')}>
                  Son Yoklama {siralama === 'son_yoklama_yeni' ? '↓' : siralama === 'son_yoklama_eski' ? '↑' : '↕'}
                </th>
                <th style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, color: textMuted, fontWeight: 500 }}>Durum</th>
                {superAdmin && <th style={{ padding: '8px 14px' }} />}
              </tr>
            </thead>
            <tbody>
              {filtrelenmis.map((o, i) => (
                <tr key={o.id} style={{ borderBottom: i < filtrelenmis.length - 1 ? `1px solid ${borderSub}` : 'none' }}>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {o.foto_url
                        ? <img src={fotoUrl(o.foto_url)} alt="" style={{ width: 28, height: 28, borderRadius: '50%' }} />
                        : <div style={{ width: 28, height: 28, borderRadius: '50%', background: isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE', color: '#3C3489', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>
                            {o.ad?.charAt(0)}{o.soyad?.charAt(0)}
                          </div>
                      }
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontWeight: 500, color: '#534AB7', cursor: 'pointer' }}
                            onClick={() => navigate(`/ogrenciler/${o.id}`)}>
                            {o.ad} {o.soyad}
                          </span>
                          {o.mobil_app && (
                            <span title="Mobil uygulama yüklü" style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              background: isDark ? 'rgba(34,197,94,0.15)' : '#f0fdf4',
                              color: '#16a34a', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 700,
                            }}>
                              📱
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: textMuted }}>{o.email}</div>
                      </div>
                    </div>
                  </td>
                  {superAdmin && (
                    <td style={{ padding: '10px 14px', fontSize: 12 }}>
                      <div style={{ color: o.sube_adi ? (isDark ? '#bbb' : '#555') : '#A32D2D', whiteSpace: 'nowrap' }}>{o.sube_adi || 'Şubesiz'}</div>
                      {o.ek_subeler?.map((s: any) => (
                        <span key={s.id} title="Ek şube" style={{ display: 'inline-block', marginTop: 3, marginRight: 4, background: isDark ? 'rgba(245,158,11,0.15)' : '#FEF3C7', color: '#92400E', borderRadius: 20, padding: '1px 7px', fontSize: 10 }}>
                          + {s.ad}
                        </span>
                      ))}
                    </td>
                  )}
                  <td style={{ padding: '10px 14px', color: isDark ? '#bbb' : '#555' }}>{o.seviye}</td>
                  <td style={{ padding: '10px 14px' }}>
                    {o.dersler?.length > 0
                      ? o.dersler.map((d: any) => (
                          <span key={d.ders_id} style={{ display: 'inline-block', background: isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE', color: '#3C3489', borderRadius: 20, padding: '2px 8px', fontSize: 11, marginRight: 4 }}>
                            {d.ders_adi}: {d.kalan_hak} hak
                          </span>
                        ))
                      : <span style={{ color: textMuted, fontSize: 11 }}>Kayıt yok</span>
                    }
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: 12 }}>
                    {o.son_yoklama_tarihi ? (
                      <div>
                        <div style={{ whiteSpace: 'nowrap', color: isDark ? '#bbb' : '#555' }}>
                          {new Date(o.son_yoklama_tarihi).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </div>
                        {o.son_yoklama_ders && <div style={{ fontSize: 11, color: textMuted, marginTop: 1 }}>{o.son_yoklama_ders}</div>}
                      </div>
                    ) : <span style={{ color: isDark ? '#444' : '#ccc' }}>—</span>}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    {(() => { const aktif = o.dersler?.length > 0; return (
                      <span style={{ background: aktif ? (isDark ? 'rgba(29,158,117,0.15)' : '#EAF3DE') : (isDark ? '#222222' : '#f0f0f0'), color: aktif ? '#3B6D11' : textSec, borderRadius: 20, padding: '2px 8px', fontSize: 11 }}>
                        {aktif ? 'Aktif' : 'Pasif'}
                      </span>
                    ); })()}
                  </td>
                  {superAdmin && (
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      <button onClick={() => subeModalAc(o)}
                        style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #534AB7', background: 'transparent', color: '#534AB7', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        Şube
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Şube modalı (süper admin) */}
      {subeModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={e => { if (e.target === e.currentTarget && !subeIslem) setSubeModal(null); }}>
          <div style={{ background: card, borderRadius: 12, padding: 24, width: 420, maxWidth: 'calc(100vw - 32px)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{subeModal.ad} {subeModal.soyad}</div>
            <div style={{ fontSize: 12, color: textSec, marginBottom: 18 }}>Şube işlemleri</div>

            <div style={{ fontSize: 11, color: textSec, marginBottom: 4, fontWeight: 600 }}>ANA ŞUBE</div>
            <div style={{ fontSize: 11, color: textMuted, marginBottom: 6 }}>Öğrenci bu şubeye taşınır, eski şubesi artık onu görmez.</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              <select value={yeniAnaSube} onChange={e => setYeniAnaSube(e.target.value)}
                style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, background: card, color: textPrimary }}>
                {!subeModal.sube_id && <option value="">— Şubesiz —</option>}
                {subeler.map(s => <option key={s.id} value={s.id}>{s.ad}</option>)}
              </select>
              <button disabled={subeIslem || !yeniAnaSube || yeniAnaSube === subeModal.sube_id}
                onClick={() => {
                  const ad = subeler.find(s => s.id === yeniAnaSube)?.ad;
                  if (!window.confirm(`${subeModal.ad} ${subeModal.soyad} → ${ad}\n\nAna şube değişecek. Emin misiniz?`)) return;
                  subeIslemiYap(() => api.patch(`/api/ogrenciler/${subeModal.id}/sube`, { sube_id: yeniAnaSube }, { timeout: 15000 }), 'Ana şube değiştirildi');
                }}
                style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: '#534AB7', color: 'white', fontSize: 12, cursor: 'pointer',
                  opacity: subeIslem || !yeniAnaSube || yeniAnaSube === subeModal.sube_id ? 0.5 : 1 }}>
                Taşı
              </button>
            </div>

            <div style={{ fontSize: 11, color: textSec, marginBottom: 4, fontWeight: 600 }}>EK ŞUBELER</div>
            <div style={{ fontSize: 11, color: textMuted, marginBottom: 8 }}>Öğrenci ana şubesinde kalır, eklenen şubenin admini de onu görür.</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {subeModal.ek_subeler?.length ? subeModal.ek_subeler.map((s: any) => (
                <span key={s.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: isDark ? 'rgba(245,158,11,0.15)' : '#FEF3C7', color: '#92400E', borderRadius: 20, padding: '3px 6px 3px 10px', fontSize: 12 }}>
                  {s.ad}
                  <button disabled={subeIslem} title="Ek şubeden çıkar"
                    onClick={() => {
                      if (!window.confirm(`${s.ad} ek şubesinden çıkarılsın mı?`)) return;
                      subeIslemiYap(() => api.delete(`/api/ogrenciler/${subeModal.id}/ek-subeler/${s.id}`, { timeout: 15000 }), 'Ek şube çıkarıldı');
                    }}
                    style={{ border: 'none', background: 'transparent', color: '#92400E', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
                </span>
              )) : <span style={{ fontSize: 12, color: textMuted }}>Ek şube yok</span>}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <select value={yeniEkSube} onChange={e => setYeniEkSube(e.target.value)}
                style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, background: card, color: textPrimary }}>
                <option value="">Şube seçin...</option>
                {subeler.filter(s => s.id !== subeModal.sube_id && !subeModal.ek_subeler?.some((e: any) => e.id === s.id))
                  .map(s => <option key={s.id} value={s.id}>{s.ad}</option>)}
              </select>
              <button disabled={subeIslem || !yeniEkSube}
                onClick={() => subeIslemiYap(() => api.post(`/api/ogrenciler/${subeModal.id}/ek-subeler`, { sube_id: yeniEkSube }, { timeout: 15000 }), 'Ek şube eklendi')}
                style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: '#F59E0B', color: 'white', fontSize: 12, cursor: 'pointer', opacity: subeIslem || !yeniEkSube ? 0.5 : 1 }}>
                + Ekle
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 22 }}>
              <button onClick={() => setSubeModal(null)} disabled={subeIslem}
                style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #ddd', background: card, color: textPrimary, cursor: 'pointer', fontSize: 13 }}>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={e => { if (e.target === e.currentTarget) setModal(false); }}>
          <div style={{ background: card, borderRadius: 12, padding: 24, width: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Yeni öğrenci ekle</div>

            {[
              { label: 'Ad', key: 'ad', placeholder: 'Örn: Sofia' },
              { label: 'Soyad', key: 'soyad', placeholder: 'Örn: Yılmaz' },
              { label: 'E-posta', key: 'email', placeholder: 'ornek@gmail.com' },
              { label: 'Telefon', key: 'tel', placeholder: '05xx xxx xx xx' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: textSec, marginBottom: 4, fontWeight: 500 }}>{f.label}</div>
                <input
                  value={(form as any)[f.key]}
                  onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }}
                />
              </div>
            ))}

            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: textSec, marginBottom: 4, fontWeight: 500 }}>Seviye</div>
                <select value={form.seviye} onChange={e => setForm(prev => ({ ...prev, seviye: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }}>
                  <option>Başlangıç</option>
                  <option>Orta</option>
                  <option>İleri</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: textSec, marginBottom: 4, fontWeight: 500 }}>Cinsiyet</div>
                <select value={form.cinsiyet} onChange={e => setForm(prev => ({ ...prev, cinsiyet: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }}>
                  <option value=''>—</option>
                  <option value='kadin'>Kadın</option>
                  <option value='erkek'>Erkek</option>
                  <option value='diger'>Diğer</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: textSec, marginBottom: 4, fontWeight: 500 }}>Doğum tarihi</div>
              <input type='date' value={form.dogum_tarihi} onChange={e => setForm(prev => ({ ...prev, dogum_tarihi: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
            </div>

            {hata && <div style={{ background: isDark ? 'rgba(228,75,74,0.15)' : '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 10 }}>
              {hata}
              {mevcutOgrenciId && (
                <button onClick={() => navigate(`/ogrenciler/${mevcutOgrenciId}`)}
                  style={{ marginLeft: 8, background: 'none', border: 'none', color: '#534AB7', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 }}>
                  Profili aç →
                </button>
              )}
            </div>}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setModal(false)} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #ddd', background: card, cursor: 'pointer', fontSize: 13 }}>İptal</button>
              <button onClick={kaydet} disabled={kaydediliyor}
                style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#534AB7', color: 'white', cursor: 'pointer', fontSize: 13, opacity: kaydediliyor ? 0.7 : 1 }}>
                {kaydediliyor ? 'Kaydediliyor...' : 'Öğrenci ekle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ogrenciler;
